import { useEffect } from "react";
import { useMediaState } from "@vidstack/react";
import type { MediaPlayerInstance } from "@vidstack/react";
import { getPlayerActions } from "@/lib/player/player.store";

/**
 * usePlayerSync Hook
 *
 * Syncs Vidstack player state with Zustand store.
 * This hook bridges the gap between Vidstack's internal state
 * and our global player store, ensuring they stay in sync.
 *
 * @param playerRef - Reference to the Vidstack MediaPlayer instance
 * @returns Object containing Vidstack state values and callback ref setter
 */

export function usePlayerSync(
	playerRef: React.RefObject<MediaPlayerInstance | null>,
) {
	// Get Vidstack state using their hooks
	const vidstackPaused = useMediaState("paused", playerRef);
	const vidstackCurrentTime = useMediaState("currentTime", playerRef);
	const vidstackDuration = useMediaState("duration", playerRef);
	const vidstackVolume = useMediaState("volume", playerRef);
	const vidstackBufferedEnd = useMediaState("bufferedEnd", playerRef);
	const vidstackWaiting = useMediaState("waiting", playerRef);

	// Cleanup player ref from store on unmount
	useEffect(() => {
		return () => {
			getPlayerActions().setPlayerRef(null);
		};
	}, []);

	// Sync paused state
	useEffect(() => {
		getPlayerActions().updatePlaybackState(vidstackPaused);
	}, [vidstackPaused]);

	// Sync current time
	useEffect(() => {
		getPlayerActions().updateCurrentTime(vidstackCurrentTime);
	}, [vidstackCurrentTime]);

	// Sync duration
	useEffect(() => {
		getPlayerActions().updateDuration(vidstackDuration);
	}, [vidstackDuration]);

	// Sync volume
	useEffect(() => {
		getPlayerActions().updateVolume(vidstackVolume);
	}, [vidstackVolume]);

	// Sync buffered end
	useEffect(() => {
		getPlayerActions().updateBufferedEnd(vidstackBufferedEnd);
	}, [vidstackBufferedEnd]);

	// Return Vidstack state for components that need it
	return {
		vidstackPaused,
		vidstackCurrentTime,
		vidstackDuration,
		vidstackVolume,
		vidstackBufferedEnd,
		vidstackWaiting,
	};
}
